import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  setDoc,
  query,
  orderBy,
  limit,
  where,
  increment,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  startAfter
} from 'firebase/firestore';
import { db } from './firebase';

// Collection names
const COLLECTIONS = {
  HUNT_ITEMS: 'huntItems',
  STORIES: 'stories',
  DOMAINS: 'domains',
  USERS: 'users',
  VOTES: 'votes',
  COMMENTS: 'comments',
  FEATURE_REQUESTS: 'featureRequests',
  USER_ACTIVITIES: 'userActivities'
};

// ===== HUNT ITEMS =====

export async function createHuntItem(data, userId) {
  const huntItem = {
    ...data,
    authorId: userId,
    votes: 0,
    comments: 0,
    createdAt: serverTimestamp(),
    upvoters: [],
    downvoters: []
  };

  const docRef = await addDoc(collection(db, COLLECTIONS.HUNT_ITEMS), huntItem);

  // Track user activity
  await trackUserActivity(userId, 'idea', `Posted "${data.title}"`, { ideaId: docRef.id, votes: 0 });

  return docRef.id;
}

export async function getHuntItems(limitCount = 20, lastDoc = null) {
  let q = query(
    collection(db, COLLECTIONS.HUNT_ITEMS),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  if (lastDoc) {
    q = query(
      collection(db, COLLECTIONS.HUNT_ITEMS),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(limitCount)
    );
  }

  const snapshot = await getDocs(q);

  // Fetch real comment counts and calculate real votes for each item
  const itemsWithComments = await Promise.all(
    snapshot.docs.map(async (doc) => {
      const data = doc.data();
      const commentsQuery = query(
        collection(db, COLLECTIONS.COMMENTS),
        where('itemId', '==', doc.id),
        where('itemType', '==', 'huntItem')
      );
      const commentsSnapshot = await getDocs(commentsQuery);

      // Calculate real vote count from upvoters and downvoters arrays
      const upvotes = data.upvoters?.length || 0;
      const downvotes = data.downvoters?.length || 0;
      const realVotes = upvotes - downvotes;

      return {
        id: doc.id,
        ...data,
        comments: commentsSnapshot.size, // Real count from database
        votes: realVotes, // Real vote count calculated from arrays
        createdAt: data.createdAt?.toDate()
      };
    })
  );

  return {
    items: itemsWithComments,
    lastDoc: snapshot.docs[snapshot.docs.length - 1],
    hasMore: snapshot.docs.length === limitCount
  };
}

export async function getHuntItemsByTimePeriod(startDate, endDate, limitCount = 20) {
  // Query items in the time period, ordered by createdAt descending
  const q = query(
    collection(db, COLLECTIONS.HUNT_ITEMS),
    where('createdAt', '>=', startDate),
    where('createdAt', '<', endDate),
    orderBy('createdAt', 'desc'),
    limit(100) // Fetch up to 100 items to sort by votes
  );

  const snapshot = await getDocs(q);

  // Fetch real comment counts and calculate real votes for each item
  const itemsWithComments = await Promise.all(
    snapshot.docs.map(async (doc) => {
      const data = doc.data();
      const commentsQuery = query(
        collection(db, COLLECTIONS.COMMENTS),
        where('itemId', '==', doc.id),
        where('itemType', '==', 'huntItem')
      );
      const commentsSnapshot = await getDocs(commentsQuery);

      // Calculate real vote count from upvoters and downvoters arrays
      const upvotes = data.upvoters?.length || 0;
      const downvotes = data.downvoters?.length || 0;
      const realVotes = upvotes - downvotes;

      return {
        id: doc.id,
        ...data,
        comments: commentsSnapshot.size,
        votes: realVotes,
        createdAt: data.createdAt?.toDate()
      };
    })
  );

  // Sort by votes descending, then by date
  itemsWithComments.sort((a, b) => {
    const voteDiff = (b.votes || 0) - (a.votes || 0);
    if (voteDiff !== 0) return voteDiff;
    return (b.createdAt || new Date(0)) - (a.createdAt || new Date(0));
  });

  // Return top items limited by count
  return itemsWithComments.slice(0, limitCount);
}

export async function getHuntItemById(itemId) {
  const itemRef = doc(db, COLLECTIONS.HUNT_ITEMS, itemId);
  const itemDoc = await getDoc(itemRef);

  if (!itemDoc.exists()) {
    return null;
  }

  const data = itemDoc.data();

  // Fetch real comment count
  const commentsQuery = query(
    collection(db, COLLECTIONS.COMMENTS),
    where('itemId', '==', itemId),
    where('itemType', '==', 'huntItem')
  );
  const commentsSnapshot = await getDocs(commentsQuery);

  // Calculate real vote count from upvoters and downvoters arrays
  const upvotes = data.upvoters?.length || 0;
  const downvotes = data.downvoters?.length || 0;
  const realVotes = upvotes - downvotes;

  return {
    id: itemDoc.id,
    ...data,
    comments: commentsSnapshot.size,
    votes: realVotes,
    createdAt: data.createdAt?.toDate()
  };
}

export async function voteHuntItem(itemId, userId, voteType) {
  const itemRef = doc(db, COLLECTIONS.HUNT_ITEMS, itemId);
  const itemDoc = await getDoc(itemRef);

  if (!itemDoc.exists()) {
    throw new Error('Item not found');
  }

  const data = itemDoc.data();
  const hasUpvoted = data.upvoters?.includes(userId);
  const hasDownvoted = data.downvoters?.includes(userId);

  let updates = {};

  if (voteType === 'up') {
    if (hasUpvoted) {
      // Remove upvote
      updates = {
        votes: increment(-1),
        upvoters: arrayRemove(userId)
      };
    } else {
      // Add upvote (remove downvote if exists)
      updates = {
        votes: increment(hasDownvoted ? 2 : 1),
        upvoters: arrayUnion(userId),
        ...(hasDownvoted && { downvoters: arrayRemove(userId) })
      };
    }
  } else if (voteType === 'down') {
    if (hasDownvoted) {
      // Remove downvote
      updates = {
        votes: increment(1),
        downvoters: arrayRemove(userId)
      };
    } else {
      // Add downvote (remove upvote if exists)
      updates = {
        votes: increment(hasUpvoted ? -2 : -1),
        downvoters: arrayUnion(userId),
        ...(hasUpvoted && { upvoters: arrayRemove(userId) })
      };
    }
  }

  await updateDoc(itemRef, updates);
}

// ===== STORIES =====

export async function createStory(data, userId) {
  const story = {
    ...data,
    authorId: userId,
    support: 0,
    supporters: [],
    comments: 0,
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, COLLECTIONS.STORIES), story);

  // Track user activity
  await trackUserActivity(userId, 'story', `Shared "${data.title}"`, { storyId: docRef.id, supports: 0 });

  return docRef.id;
}

export async function getStories(limitCount = 20, lastDoc = null) {
  let q = query(
    collection(db, COLLECTIONS.STORIES),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  if (lastDoc) {
    q = query(
      collection(db, COLLECTIONS.STORIES),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(limitCount)
    );
  }

  const snapshot = await getDocs(q);

  // Fetch real comment counts and calculate real support for each story
  const storiesWithComments = await Promise.all(
    snapshot.docs.map(async (doc) => {
      const data = doc.data();
      const commentsQuery = query(
        collection(db, COLLECTIONS.COMMENTS),
        where('itemId', '==', doc.id),
        where('itemType', '==', 'story')
      );
      const commentsSnapshot = await getDocs(commentsQuery);

      // Calculate real support count from supporters array
      const realSupport = data.supporters?.length || 0;

      return {
        id: doc.id,
        ...data,
        comments: commentsSnapshot.size, // Real count from database
        support: realSupport, // Real support count from supporters array
        createdAt: data.createdAt?.toDate()
      };
    })
  );

  return {
    items: storiesWithComments,
    lastDoc: snapshot.docs[snapshot.docs.length - 1],
    hasMore: snapshot.docs.length === limitCount
  };
}

export async function getStoryById(storyId) {
  const storyRef = doc(db, COLLECTIONS.STORIES, storyId);
  const storyDoc = await getDoc(storyRef);

  if (!storyDoc.exists()) {
    return null;
  }

  const data = storyDoc.data();

  // Fetch real comment count
  const commentsQuery = query(
    collection(db, COLLECTIONS.COMMENTS),
    where('itemId', '==', storyId),
    where('itemType', '==', 'story')
  );
  const commentsSnapshot = await getDocs(commentsQuery);

  // Calculate real support count from supporters array
  const realSupport = data.supporters?.length || 0;

  return {
    id: storyDoc.id,
    ...data,
    comments: commentsSnapshot.size,
    support: realSupport,
    createdAt: data.createdAt?.toDate()
  };
}

export async function supportStory(storyId, userId) {
  const storyRef = doc(db, COLLECTIONS.STORIES, storyId);
  const storyDoc = await getDoc(storyRef);

  if (!storyDoc.exists()) {
    throw new Error('Story not found');
  }

  const data = storyDoc.data();
  const hasSupported = data.supporters?.includes(userId);

  if (hasSupported) {
    // Remove support
    await updateDoc(storyRef, {
      support: increment(-1),
      supporters: arrayRemove(userId)
    });
  } else {
    // Add support
    await updateDoc(storyRef, {
      support: increment(1),
      supporters: arrayUnion(userId)
    });
  }
}

// ===== DOMAINS =====

export async function createDomain(data, userId) {
  const domain = {
    ...data,
    ownerId: userId,
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, COLLECTIONS.DOMAINS), domain);

  // Track user activity
  await trackUserActivity(userId, 'domain', `Added ${data.domain} to graveyard`, { domainId: docRef.id });

  return docRef.id;
}

export async function getDomains(limitCount = 50, lastDoc = null) {
  let q = query(
    collection(db, COLLECTIONS.DOMAINS),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  if (lastDoc) {
    q = query(
      collection(db, COLLECTIONS.DOMAINS),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(limitCount)
    );
  }

  const snapshot = await getDocs(q);
  return {
    items: snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    })),
    lastDoc: snapshot.docs[snapshot.docs.length - 1],
    hasMore: snapshot.docs.length === limitCount
  };
}

export async function deleteDomain(domainId, userId) {
  const domainRef = doc(db, COLLECTIONS.DOMAINS, domainId);
  const domainDoc = await getDoc(domainRef);

  if (!domainDoc.exists()) {
    throw new Error('Domain not found');
  }

  const data = domainDoc.data();
  if (data.ownerId !== userId) {
    throw new Error('Not authorized to delete this domain');
  }

  await deleteDoc(domainRef);
}

// ===== USER PROFILE =====

export async function getUserProfile(userId) {
  const userRef = doc(db, COLLECTIONS.USERS, userId);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    return { id: userDoc.id, ...userDoc.data() };
  }
  return null;
}

export async function createOrUpdateUserProfile(userId, data) {
  const userRef = doc(db, COLLECTIONS.USERS, userId);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    // Update existing profile
    await updateDoc(userRef, {
      ...data,
      lastActive: serverTimestamp()
    });
  } else {
    // Create new profile
    await setDoc(userRef, {
      ...data,
      createdAt: serverTimestamp(),
      lastActive: serverTimestamp()
    });
  }
}

// ===== COMMENTS =====

export async function createComment(itemId, itemType, userId, userName, text) {
  const comment = {
    itemId,
    itemType, // 'huntItem', 'story', 'domain'
    userId,
    userName,
    text,
    likes: 0,
    likers: [],
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, COLLECTIONS.COMMENTS), comment);

  // Increment comment count on the item
  const itemCollection = itemType === 'huntItem' ? COLLECTIONS.HUNT_ITEMS :
                         itemType === 'story' ? COLLECTIONS.STORIES :
                         COLLECTIONS.DOMAINS;
  const itemRef = doc(db, itemCollection, itemId);
  await updateDoc(itemRef, {
    comments: increment(1)
  });

  // Track user activity
  await trackUserActivity(userId, 'comment', `Commented on ${itemType}`);

  return docRef.id;
}

export async function getComments(itemId, limitCount = 50) {
  // First get all comments for this item
  const q = query(
    collection(db, COLLECTIONS.COMMENTS),
    where('itemId', '==', itemId),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  // Calculate real likes from likers array and sort by likes then createdAt
  const comments = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      likes: data.likers?.length || 0, // Real count from likers array
      createdAt: data.createdAt?.toDate()
    };
  });

  // Sort by likes descending, then by createdAt descending
  comments.sort((a, b) => {
    if (b.likes !== a.likes) {
      return b.likes - a.likes;
    }
    return (b.createdAt || new Date(0)) - (a.createdAt || new Date(0));
  });

  return comments;
}

export async function likeComment(commentId, userId) {
  const commentRef = doc(db, COLLECTIONS.COMMENTS, commentId);
  const commentDoc = await getDoc(commentRef);

  if (!commentDoc.exists()) {
    throw new Error('Comment not found');
  }

  const data = commentDoc.data();
  const hasLiked = data.likers?.includes(userId);

  if (hasLiked) {
    // Remove like
    const newLikers = (data.likers || []).filter(id => id !== userId);
    await updateDoc(commentRef, {
      likers: newLikers,
      likes: newLikers.length
    });
  } else {
    // Add like
    const newLikers = [...(data.likers || []), userId];
    await updateDoc(commentRef, {
      likers: newLikers,
      likes: newLikers.length
    });
  }
}

// ===== FEATURE REQUESTS =====

export async function createFeatureRequest(itemId, userId, userName, text) {
  const featureRequest = {
    itemId,
    userId,
    userName,
    text,
    votes: 0,
    voters: [],
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, COLLECTIONS.FEATURE_REQUESTS), featureRequest);

  // Track user activity
  await trackUserActivity(userId, 'feature-request', 'Requested a feature');

  return docRef.id;
}

export async function getFeatureRequests(itemId, limitCount = 20) {
  // First get all feature requests for this item
  const q = query(
    collection(db, COLLECTIONS.FEATURE_REQUESTS),
    where('itemId', '==', itemId),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  // Calculate real votes from voters array and sort by votes then createdAt
  const features = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      votes: data.voters?.length || 0, // Real count from voters array
      createdAt: data.createdAt?.toDate()
    };
  });

  // Sort by votes descending, then by createdAt descending
  features.sort((a, b) => {
    if (b.votes !== a.votes) {
      return b.votes - a.votes;
    }
    return (b.createdAt || new Date(0)) - (a.createdAt || new Date(0));
  });

  return features;
}

export async function voteFeatureRequest(featureId, userId) {
  const featureRef = doc(db, COLLECTIONS.FEATURE_REQUESTS, featureId);
  const featureDoc = await getDoc(featureRef);

  if (!featureDoc.exists()) {
    throw new Error('Feature request not found');
  }

  const data = featureDoc.data();
  const hasVoted = data.voters?.includes(userId);

  if (hasVoted) {
    // Remove vote
    await updateDoc(featureRef, {
      voters: arrayRemove(userId)
    });
  } else {
    // Add vote
    await updateDoc(featureRef, {
      voters: arrayUnion(userId)
    });
  }
}

// ===== USER ACTIVITIES =====

export async function trackUserActivity(userId, type, description, metadata = {}) {
  const activity = {
    userId,
    type, // 'idea', 'story', 'domain', 'vote', 'comment', 'feature-request'
    description,
    metadata,
    createdAt: serverTimestamp()
  };

  await addDoc(collection(db, COLLECTIONS.USER_ACTIVITIES), activity);
}

export async function getUserActivities(userId, limitCount = 20) {
  const q = query(
    collection(db, COLLECTIONS.USER_ACTIVITIES),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate()
  }));
}

// ===== USER STATS =====

export async function calculateUserStats(userId) {
  // Get user's hunt items
  const huntItemsQuery = query(
    collection(db, COLLECTIONS.HUNT_ITEMS),
    where('authorId', '==', userId)
  );
  const huntItemsSnapshot = await getDocs(huntItemsQuery);
  const huntItems = huntItemsSnapshot.docs.map(doc => doc.data());

  // Get user's stories
  const storiesQuery = query(
    collection(db, COLLECTIONS.STORIES),
    where('authorId', '==', userId)
  );
  const storiesSnapshot = await getDocs(storiesQuery);

  // Get user's domains
  const domainsQuery = query(
    collection(db, COLLECTIONS.DOMAINS),
    where('ownerId', '==', userId)
  );
  const domainsSnapshot = await getDocs(domainsQuery);

  // Get user's comments
  const commentsQuery = query(
    collection(db, COLLECTIONS.COMMENTS),
    where('userId', '==', userId)
  );
  const commentsSnapshot = await getDocs(commentsQuery);

  // Calculate upvotes received
  const upvotesReceived = huntItems.reduce((sum, item) => sum + (item.votes || 0), 0);

  // Calculate supports given
  const supportsGivenQuery = query(
    collection(db, COLLECTIONS.STORIES),
    where('supporters', 'array-contains', userId)
  );
  const supportsGivenSnapshot = await getDocs(supportsGivenQuery);

  // Get user profile for member since date
  const userProfile = await getUserProfile(userId);

  const stats = {
    ideasPosted: huntItemsSnapshot.size,
    storiesShared: storiesSnapshot.size,
    domainsAdded: domainsSnapshot.size,
    upvotesReceived,
    supportsGiven: supportsGivenSnapshot.size,
    commentsPosted: commentsSnapshot.size,
    totalPoints: upvotesReceived + (storiesSnapshot.size * 10) + (domainsSnapshot.size * 5) + (commentsSnapshot.size * 2),
    memberSince: userProfile?.createdAt || new Date()
  };

  return stats;
}

// ===== FIX COMMENT COUNTS =====

export async function fixAllCommentCounts() {
  try {
    console.log('Starting to fix comment counts...');

    // Fix hunt items
    const huntItemsSnapshot = await getDocs(collection(db, COLLECTIONS.HUNT_ITEMS));
    for (const itemDoc of huntItemsSnapshot.docs) {
      const commentsQuery = query(
        collection(db, COLLECTIONS.COMMENTS),
        where('itemId', '==', itemDoc.id),
        where('itemType', '==', 'huntItem')
      );
      const commentsSnapshot = await getDocs(commentsQuery);
      const actualCount = commentsSnapshot.size;

      await updateDoc(doc(db, COLLECTIONS.HUNT_ITEMS, itemDoc.id), {
        comments: actualCount
      });
      console.log(`Fixed hunt item ${itemDoc.id}: ${actualCount} comments`);
    }

    // Fix stories
    const storiesSnapshot = await getDocs(collection(db, COLLECTIONS.STORIES));
    for (const storyDoc of storiesSnapshot.docs) {
      const commentsQuery = query(
        collection(db, COLLECTIONS.COMMENTS),
        where('itemId', '==', storyDoc.id),
        where('itemType', '==', 'story')
      );
      const commentsSnapshot = await getDocs(commentsQuery);
      const actualCount = commentsSnapshot.size;

      await updateDoc(doc(db, COLLECTIONS.STORIES, storyDoc.id), {
        comments: actualCount
      });
      console.log(`Fixed story ${storyDoc.id}: ${actualCount} comments`);
    }

    console.log('Comment counts fixed successfully!');
    return { success: true };
  } catch (error) {
    console.error('Error fixing comment counts:', error);
    throw error;
  }
}

// ===== ACHIEVEMENTS =====

export async function checkAndAwardAchievements(userId, stats) {
  const achievements = [];

  if (stats.ideasPosted >= 1) achievements.push('first-post');
  if (stats.ideasPosted >= 10) achievements.push('idea-machine');
  if (stats.upvotesReceived >= 100) achievements.push('viral-drunk');
  if (stats.storiesShared >= 5) achievements.push('storyteller');
  if (stats.domainsAdded >= 5) achievements.push('domain-collector');
  if (stats.supportsGiven >= 50) achievements.push('supportive-drunk');

  // Check for vote master (need to query votes)
  const huntItemsWithVote = query(
    collection(db, COLLECTIONS.HUNT_ITEMS),
    where('upvoters', 'array-contains', userId)
  );
  const votedItems = await getDocs(huntItemsWithVote);
  if (votedItems.size >= 100) achievements.push('vote-master');

  // Check for night owl (posted between 2am-4am)
  const activitiesQuery = query(
    collection(db, COLLECTIONS.USER_ACTIVITIES),
    where('userId', '==', userId)
  );
  const activities = await getDocs(activitiesQuery);
  const hasNightPost = activities.docs.some(doc => {
    const hour = doc.data().createdAt?.toDate().getHours();
    return hour >= 2 && hour < 4;
  });
  if (hasNightPost) achievements.push('night-owl');

  return achievements;
}
